/**
 * MJ — variant rules.
 *
 * Rules a customer would otherwise have to work out for themselves: choose
 * Online, and a training kit cannot be posted to you, so "With kit" is not a
 * real choice. Rather than let them pick it and meet an unavailable variant,
 * the rule takes the decision: the ruled-out values are struck out and made
 * unpickable, and the allowed one is selected for them.
 *
 * Each rule is read as:   Option = Value [& Option = Value] -> Option = Value
 *
 * The picker is Horizon's and is re-rendered from the server on every
 * change, so nothing is cached: the inputs are looked up again each pass, and
 * a MutationObserver re-runs the rules once the new markup lands.
 */
class MjVariantRules extends HTMLElement {
  connectedCallback() {
    try {
      this.rules = JSON.parse(this.dataset.rules || '[]');
    } catch (error) {
      this.rules = [];
    }
    if (!this.rules.length) return;

    this.apply = this.apply.bind(this);
    this.schedule = this.schedule.bind(this);

    this.picker = this.#findPicker();
    if (!this.picker) return;

    this.picker.addEventListener('change', this.schedule);

    // Horizon replaces the picker's markup wholesale after each change, which
    // takes our attributes with it.
    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(this.picker, { childList: true, subtree: true });

    this.apply();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.picker?.removeEventListener('change', this.schedule);
    window.clearTimeout(this.timer);
  }

  #findPicker() {
    const section = this.closest('.shopify-section') || document;
    return section.querySelector('variant-picker');
  }

  schedule() {
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(this.apply, 60);
  }

  /** Every radio in the picker, grouped by the option name it belongs to. */
  #inputs() {
    const groups = new Map();
    for (const input of this.picker.querySelectorAll('input[type="radio"][data-option-name]')) {
      const name = this.#key(input.dataset.optionName);
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(input);
    }
    return groups;
  }

  #key(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  apply() {
    if (!this.picker || this.applying) return;
    this.applying = true;

    const groups = this.#inputs();

    // Start from a clean slate: a rule that no longer matches must give the
    // values it was blocking back.
    for (const inputs of groups.values()) {
      for (const input of inputs) this.#block(input, false);
    }

    let toSelect = null;

    for (const rule of this.rules) {
      if (!this.#matches(rule, groups)) continue;

      const targets = groups.get(this.#key(rule.option));
      if (!targets) continue;

      const wanted = this.#key(rule.value);
      let allowed = null;

      for (const input of targets) {
        if (this.#key(input.value) === wanted) allowed = input;
        else this.#block(input, true);
      }

      // The rule is only worth acting on if the value it allows exists.
      if (allowed && !allowed.checked && !toSelect) toSelect = allowed;
    }

    this.applying = false;

    // A click, not input.checked — the picker listens for the change event,
    // and setting the property does not fire one.
    if (toSelect) toSelect.click();
  }

  #matches(rule, groups) {
    for (const condition of rule.when) {
      const inputs = groups.get(this.#key(condition.option));
      if (!inputs) return false;

      const checked = inputs.find((input) => input.checked);
      if (!checked || this.#key(checked.value) !== this.#key(condition.value)) return false;
    }
    return rule.when.length > 0;
  }

  #block(input, blocked) {
    const tile = input.closest('.mj-tile') || input.parentElement;

    input.disabled = blocked;
    if (blocked) input.setAttribute('aria-disabled', 'true');
    else if (input.dataset.optionAvailable !== 'false') input.removeAttribute('aria-disabled');

    tile?.classList.toggle('mj-tile--ruled-out', blocked);
  }
}

if (!customElements.get('mj-variant-rules')) {
  customElements.define('mj-variant-rules', MjVariantRules);
}
